import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Page,
  Navbar,
  NavbarBackLink,
  Block,
  BlockTitle,
  List,
  ListItem,
  ListInput,
  Button,
  Segmented,
  SegmentedButton,
  Sheet,
} from 'konsta/react'
import {
  AlertTriangle,
  Clock,
  CheckCircle2,
  FileText,
  Send,
  Plus,
  Hash,
  DoorOpen,
  DollarSign,
  Calendar,
} from 'lucide-react'
import { formatCurrency, formatRelativeTime } from '@/lib/utils'

// --- Types ---

interface Dispute {
  id: string
  handId: string
  room: string
  reason: string
  status: 'pending' | 'resolved'
  amount: number
  date: Date
  description: string
  resolution?: string
}

// --- Mock Data ---

const mockDisputes: Dispute[] = [
  {
    id: 'D001',
    handId: 'H004',
    room: '高額桌 VIP',
    reason: '斷線導致自動棄牌',
    status: 'pending',
    amount: 5000,
    date: new Date(Date.now() - 1000 * 60 * 60 * 2),
    description: '在關鍵手牌中因網路斷線被系統自動棄牌，要求退還底池。',
  },
  {
    id: 'D002',
    handId: 'H012',
    room: '中級桌 #3',
    reason: '牌局結算錯誤',
    status: 'pending',
    amount: 2000,
    date: new Date(Date.now() - 1000 * 60 * 60 * 24),
    description: '底池分配金額與顯示不符。',
  },
  {
    id: 'D003',
    handId: 'H008',
    room: '新手桌 #1',
    reason: '疑似作弊行為',
    status: 'resolved',
    amount: 0,
    date: new Date(Date.now() - 1000 * 60 * 60 * 72),
    description: '對手連續多手異常操作，疑似使用外掛。',
    resolution: '經調查未發現異常行為。',
  },
  {
    id: 'D004',
    handId: 'H020',
    room: '高額桌 #1',
    reason: '底池計算錯誤',
    status: 'resolved',
    amount: 1500,
    date: new Date(Date.now() - 1000 * 60 * 60 * 120),
    description: '側池分配有誤。',
    resolution: '已確認錯誤並退款 $15.00。',
  },
]

// --- Helpers ---

function StatusBadge({ status }: { status: 'pending' | 'resolved' }) {
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-[#FF9500]/10 text-[#FF9500]">
        <Clock className="w-3 h-3" />
        審核中
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-[#34C759]/10 text-[#34C759]">
      <CheckCircle2 className="w-3 h-3" />
      已解決
    </span>
  )
}

// --- Components ---

type FilterTab = 'pending' | 'resolved' | 'all'

function DisputeDetailSheet({
  dispute,
  open,
  onClose,
}: {
  dispute: Dispute | null
  open: boolean
  onClose: () => void
}) {
  const [response, setResponse] = useState('')

  if (!dispute) return null

  return (
    <Sheet
      opened={open}
      onBackdropClick={onClose}
      className="pb-safe"
      style={{ height: 'auto', maxHeight: '85vh' }}
    >
      <div className="overflow-y-auto" style={{ maxHeight: '80vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5EA]">
          <h3 className="text-lg font-bold text-black">爭議詳情</h3>
          <button
            onClick={onClose}
            className="text-sm font-medium text-[#007AFF]"
          >
            關閉
          </button>
        </div>

        {/* Dispute Info */}
        <Block className="!mt-2">
          <div className="flex items-center justify-between mb-3">
            <span className="text-base font-bold text-black">{dispute.id}</span>
            <StatusBadge status={dispute.status} />
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-sm">
              <Hash className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">手牌</span>
              <span className="font-medium text-black ml-auto">{dispute.handId}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <DoorOpen className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">房間</span>
              <span className="font-medium text-black ml-auto">{dispute.room}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">原因</span>
              <span className="font-medium text-black ml-auto">{dispute.reason}</span>
            </div>
            {dispute.amount > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="w-4 h-4 text-[#8E8E93]" />
                <span className="text-[#8E8E93]">涉及金額</span>
                <span className="font-semibold text-[#FF3B30] ml-auto">
                  {formatCurrency(dispute.amount)}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">提交時間</span>
              <span className="font-medium text-black ml-auto">
                {formatRelativeTime(dispute.date)}
              </span>
            </div>
          </div>
        </Block>

        {/* Description */}
        <BlockTitle>描述</BlockTitle>
        <Block strong inset>
          <p className="text-sm text-black leading-relaxed">{dispute.description}</p>
        </Block>

        {/* Timeline */}
        <BlockTitle>處理進度</BlockTitle>
        <Block strong inset>
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-2.5 h-2.5 rounded-full bg-[#007AFF]" />
                <div className="w-0.5 flex-1 bg-[#E5E5EA] mt-1" />
              </div>
              <div className="pb-4">
                <p className="text-sm font-medium text-black">提交爭議</p>
                <p className="text-xs text-[#8E8E93] mt-0.5">
                  {formatRelativeTime(dispute.date)}
                </p>
              </div>
            </div>

            {dispute.status === 'pending' ? (
              <div className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FF9500]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#FF9500]">審核中</p>
                  <p className="text-xs text-[#8E8E93] mt-0.5">等待管理員處理</p>
                </div>
              </div>
            ) : (
              <div className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#34C759]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#34C759]">已解決</p>
                  <p className="text-xs text-[#8E8E93] mt-0.5">{dispute.resolution}</p>
                </div>
              </div>
            )}
          </div>
        </Block>

        {/* Response Form (only for pending disputes) */}
        {dispute.status === 'pending' && (
          <>
            <BlockTitle>補充說明</BlockTitle>
            <List strongIos insetIos>
              <ListInput
                type="textarea"
                placeholder="請輸入補充說明或證據..."
                value={response}
                onInput={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setResponse(e.target.value)
                }
                inputClassName="!h-24 resize-none"
              />
            </List>
            <Block className="!px-4 !mt-0">
              <Button large rounded>
                <span className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  提交補充
                </span>
              </Button>
            </Block>
          </>
        )}

        {/* Bottom spacer */}
        <div className="h-4" />
      </div>
    </Sheet>
  )
}

// --- Main Screen ---

export function DisputesScreen() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<FilterTab>('pending')
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'pending', label: '進行中' },
    { key: 'resolved', label: '已解決' },
    { key: 'all', label: '全部' },
  ]

  const filteredDisputes = mockDisputes.filter((d) => {
    if (activeTab === 'all') return true
    return d.status === activeTab
  })

  const pendingCount = mockDisputes.filter((d) => d.status === 'pending').length

  const handleDisputeClick = (dispute: Dispute) => {
    setSelectedDispute(dispute)
    setSheetOpen(true)
  }

  return (
    <Page className="bg-[#F2F2F7]">
      <Navbar
        title="爭議處理"
        left={<NavbarBackLink onClick={() => navigate(-1)} />}
      />

      {/* Segmented Tabs */}
      <Block className="!px-4 !mt-2 !mb-0">
        <Segmented strong rounded>
          {tabs.map((tab) => (
            <SegmentedButton
              key={tab.key}
              active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="flex items-center gap-1">
                {tab.label}
                {tab.key === 'pending' && pendingCount > 0 && (
                  <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-[#FF3B30] rounded-full">
                    {pendingCount}
                  </span>
                )}
              </span>
            </SegmentedButton>
          ))}
        </Segmented>
      </Block>

      {/* Dispute List */}
      <div className="overflow-y-auto pb-8">
        {filteredDisputes.length > 0 ? (
          <List strongIos insetIos className="!mt-4">
            {filteredDisputes.map((dispute) => (
              <ListItem
                key={dispute.id}
                link
                chevronIos
                onClick={() => handleDisputeClick(dispute)}
                title={
                  <span className="flex items-center gap-2">
                    {dispute.reason}
                  </span>
                }
                subtitle={
                  <span className="flex items-center gap-2 text-[#8E8E93]">
                    <span className="flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      {dispute.handId}
                    </span>
                    <span>|</span>
                    <span>{dispute.room}</span>
                  </span>
                }
                after={<StatusBadge status={dispute.status} />}
                media={
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      dispute.status === 'pending'
                        ? 'bg-[#FF9500]/10'
                        : 'bg-[#34C759]/10'
                    }`}
                  >
                    {dispute.status === 'pending' ? (
                      <AlertTriangle className="w-5 h-5 text-[#FF9500]" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5 text-[#34C759]" />
                    )}
                  </div>
                }
                text={
                  <span className="flex items-center gap-3 text-[#8E8E93]">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatRelativeTime(dispute.date)}
                    </span>
                    {dispute.amount > 0 && (
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        {formatCurrency(dispute.amount)}
                      </span>
                    )}
                  </span>
                }
              />
            ))}
          </List>
        ) : (
          <Block className="!mt-8">
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#E5E5EA]/50 flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-[#8E8E93]" />
              </div>
              <p className="text-base font-semibold text-[#8E8E93]">
                沒有爭議記錄
              </p>
              <p className="text-sm text-[#8E8E93]/60 mt-1">
                {activeTab === 'pending'
                  ? '目前沒有進行中的爭議'
                  : '目前沒有已解決的爭議'}
              </p>
            </div>
          </Block>
        )}

        {/* New Dispute Button */}
        <Block className="!px-4 !mt-4">
          <Button large rounded tonal>
            <span className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              提交新爭議
            </span>
          </Button>
        </Block>
      </div>

      {/* Dispute Detail Sheet */}
      <DisputeDetailSheet
        dispute={selectedDispute}
        open={sheetOpen}
        onClose={() => {
          setSheetOpen(false)
          setSelectedDispute(null)
        }}
      />
    </Page>
  )
}
